import { AnalysisCommentMutation, GameReviewMutation, InternalAnalysisComment, InternalGameReview } from '../features/review/types/InternalReview'
import { customError, optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

// A missing review or comment answers 204 with an empty body.
const reviewRequest = (id: string) => ({ url: `i/gamereviews/${id}`, responseHandler: optionalJson })
const commentRequest = (id: string) => ({ url: `i/analysiscomments/${id}`, responseHandler: optionalJson })

export const notStoredError = 'not stored'
export const stillStoredError = 'still stored'

const reviewsApi = internalApiserverApi
  .enhanceEndpoints({ addTagTypes: ['internalGameReview', 'internalAnalysisComment', 'internalGameDocument', 'gameAnalyses'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInternalGameReview: builder.query<InternalGameReview | null, string>({
        query: reviewRequest,
        providesTags: (_result, _error, id) => [{ type: 'internalGameReview', id }],
      }),
      // Creates with `id: null`. Returns the review read again by the answer's id.
      saveGameReview: builder.mutation<InternalGameReview, GameReviewMutation>({
        async queryFn(review, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'i/gamereviews', method: 'POST', body: review, responseHandler: optionalJson })
          if (saved.error) return { error: saved.error }
          const id = (saved.data as { id?: string } | null)?.id ?? review.id
          if (!id) return { error: customError(notStoredError) }
          const stored = await baseQuery(reviewRequest(id))
          if (stored.error) return { error: stored.error }
          if (!stored.data) return { error: customError(notStoredError) }
          return { data: stored.data as InternalGameReview }
        },
        invalidatesTags: (result) => (result ? [{ type: 'internalGameReview', id: result.id }, 'internalGameDocument'] : []),
      }),
      // Resolves only when a fresh read no longer finds the review.
      deleteGameReview: builder.mutation<null, string>({
        async queryFn(id, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `i/gamereviews/${id}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const stored = await baseQuery(reviewRequest(id))
          if (stored.error) return { error: stored.error }
          if (stored.data) return { error: customError(stillStoredError) }
          return { data: null }
        },
        invalidatesTags: (_result, error, id) => (error ? [] : [{ type: 'internalGameReview', id }, 'internalGameDocument']),
      }),
      getInternalAnalysisComment: builder.query<InternalAnalysisComment | null, string>({
        query: commentRequest,
        providesTags: (_result, _error, id) => [{ type: 'internalAnalysisComment', id }],
      }),
      // Creates with `id: null`. Returns the comment read again by the answer's id.
      saveAnalysisComment: builder.mutation<InternalAnalysisComment, AnalysisCommentMutation>({
        async queryFn(comment, _api, _extraOptions, baseQuery) {
          const saved = await baseQuery({ url: 'i/analysiscomments', method: 'POST', body: comment, responseHandler: optionalJson })
          if (saved.error) return { error: saved.error }
          const id = (saved.data as { id?: string } | null)?.id ?? comment.id
          if (!id) return { error: customError(notStoredError) }
          const stored = await baseQuery(commentRequest(id))
          if (stored.error) return { error: stored.error }
          if (!stored.data) return { error: customError(notStoredError) }
          return { data: stored.data as InternalAnalysisComment }
        },
        invalidatesTags: (result) =>
          result
            ? [
                { type: 'internalAnalysisComment', id: result.id },
                { type: 'gameAnalyses', id: result.gameId },
              ]
            : [],
      }),
      deleteAnalysisComment: builder.mutation<null, { id: string; gameId: string }>({
        async queryFn({ id }, _api, _extraOptions, baseQuery) {
          const deleted = await baseQuery({ url: `i/analysiscomments/${id}`, method: 'DELETE', responseHandler: 'text' })
          if (deleted.error) return { error: deleted.error }
          const stored = await baseQuery(commentRequest(id))
          if (stored.error) return { error: stored.error }
          if (stored.data) return { error: customError(stillStoredError) }
          return { data: null }
        },
        invalidatesTags: (_result, error, { id, gameId }) =>
          error
            ? []
            : [
                { type: 'internalAnalysisComment', id },
                { type: 'gameAnalyses', id: gameId },
              ],
      }),
    }),
  })

export const {
  useGetInternalGameReviewQuery,
  useSaveGameReviewMutation,
  useDeleteGameReviewMutation,
  useGetInternalAnalysisCommentQuery,
  useSaveAnalysisCommentMutation,
  useDeleteAnalysisCommentMutation,
} = reviewsApi
